import { useEffect, useMemo, useRef } from "react";
import classnames from "classnames";
import { Link } from "react-router-dom";
import { LeaderboardPagination } from "./LeaderboardPagination";
import { useTable, useSortBy, usePagination, Column, Row } from "react-table";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { getRiotAssetsContext, getS13RankUrl } from "@outplayed/riot-assets";
import { numberWithCommas } from "@ugg/shared/utils/math";
import { getTierColor, getProBuildRankName } from "@ugg/shared/utils/rank-helpers";
import { REGION_LABEL_MAP } from "../../leaderboard-filters";
import { splitRiotId } from "@ugg/shared/utils/riot-id-helpers";
import { LeaderboardPlayerPlus } from "@ugg/shared/interfaces/leaderboards.interface";

const columnStyle = (column: any) => ({
  flex: `1 1 100%`,
});

const TableSummonerRow = ({
  rowIndex,
  row,
  isSearchedSummoner,
}: {
  rowIndex: number;
  row: Row<LeaderboardPlayerPlus>;
  isSearchedSummoner: boolean;
}) => {
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isSearchedSummoner && rowRef.current) {
      setTimeout(() => {
        rowRef.current?.scrollIntoView({ block: "center" });
      }, 0);
    }
  }, []);

  return (
    <div ref={rowRef} className={classnames("rt-tr-group", { "summoner-highlighted": isSearchedSummoner })}>
      <div {...row.getRowProps({ className: "rt-tr" })}>
        {row.cells.map((cell, index) => {
          const tdClassNames = classnames("rt-td", cell.column.responsiveClassName, cell.column.className, {
            "is-in-odd-row": rowIndex % 2 === 0,
          });

          return (
            <div {...cell.getCellProps({ className: tdClassNames, style: columnStyle(cell.column) })}>
              {cell.render("Cell", { index: rowIndex })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface LeaderBoardTableProps {
  page: number;
  totalCount: number;
  tableData: {
    topFive: LeaderboardPlayerPlus[];
    remainingData: LeaderboardPlayerPlus[];
    firstEntryRanking: number;
    lastEntryRanking: number;
  };
  region?: string;
  hidePagination?: boolean;
  showRegion?: boolean;
}

const LeaderboardTable = (props: LeaderBoardTableProps) => {
  const { getProfileIconImg } = getRiotAssetsContext();
  const { page, totalCount, tableData, hidePagination, showRegion } = props;
  const queryParams = useQueryString();
  const { user, region } = queryParams;

  const { remainingData, firstEntryRanking, lastEntryRanking } = tableData || {};

  const columns = useMemo<Column<LeaderboardPlayerPlus>[]>(() => {
    return [
      {
        id: "rank",
        accessor: "overallRanking",
        Header: <span>Rank</span>,
        responsiveClassName: "max-w-[58px] max-sm:max-w-[60px]",
        headerClassName: "max-xs:[&>*]:hidden",
        Cell: ({ cell: { value } }) => (
          <span
            className={classnames("text-white text-[14px] max-sm:text-[12px]", {
              "max-xs:text-[11px]": value > 999 && value <= 99999,
              "max-xs:text-[10px]": value > 99999,
            })}
          >
            {numberWithCommas(value)}
          </span>
        ),
      },
      {
        accessor: "overallRanking",
        headerClassName: "summoner-col",
        className: "summoner-col",
        Header: "Summoner",
        responsiveClassName: "min-w-[320px] max-xs:min-w-[60px]",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => (
          <Link className="summoner" to={original.profileURL}>
            <div className="profile-icon-container">
              <img src={getProfileIconImg(original.iconId || 1)} />
            </div>
            <div className="summoner-name">{original.riotUserName}</div>
          </Link>
        ),
      },
      {
        id: "region",
        accessor: "regionId",
        headerClassName: "region",
        className: "region",
        Header: "Region",
        responsiveClassName: "min-w-[60px] max-sm:invisible max-sm:max-w-[60px]",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => (
          <div className="region">
            {original.regionId && REGION_LABEL_MAP[original.regionId]
              ? REGION_LABEL_MAP[original.regionId]
              : REGION_LABEL_MAP[region]}
          </div>
        ),
      },
      {
        id: "tier-lp_desktop",
        accessor: "lp",
        headerClassName: "tier-lp_desktop",
        className: "tier-lp_desktop",
        Header: "Tier",
        responsiveClassName: "max-w-[180px]",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => (
          <div className="tier">
            <img className="rank-img" src={getS13RankUrl(original.tier)} />
            <strong style={{ color: getTierColor(original.tier) }}>{getProBuildRankName(original.tier, original.rank)}</strong>
          </div>
        ),
      },
      {
        headerClassName: "tier-lp_desktop",
        className: "tier-lp_desktop",
        accessor: "lp",
        Header: "LP",
        responsiveClassName: "max-w-[170px]",
        Cell: ({ cell: { value } }) => <span>{numberWithCommas(value || 0)} LP</span>,
      },
      {
        // Tier and LP for mobile
        id: "tier-lp",
        accessor: "lp",
        headerClassName: "tier-lp_mobile",
        className: "tier-lp_mobile",
        responsiveClassName: "min-w-[140px] max-xs:min-w-[80px]",
        Header: () => (
          <span>
            Tier <span className="text-lavender-300">/</span> LP
          </span>
        ),
        Cell: ({
          cell: {
            row: { original },
          },
        }) => (
          <div className="tier">
            <img className="rank-img" src={getS13RankUrl(original.tier)} />
            <span>
              <strong style={{ color: getTierColor(original.tier) }}>
                {getProBuildRankName(original.tier, original.rank, true)}
              </strong>
              <span className="text-lavender-300"> / </span>
              <span>{numberWithCommas(original.lp || 0)} LP</span>
            </span>
          </div>
        ),
      },
      {
        id: "winRate",
        accessor: "overallRanking",
        Header: "Win Rate",
        responsiveClassName: "max-w-[220px] min-w-[110px] max-xs:max-w-[80px] max-xs:min-w-[50px] max-xxxs:max-w-[60px]",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => (
          <div className="win-rate">
            <div className="wr-text">
              <div className="wr-text_win-rate">{original.winRate}%</div>
              <div className="wr-text_games">
                {numberWithCommas(parseInt(original.wins))}W {numberWithCommas(parseInt(original.losses))}L
              </div>
            </div>
          </div>
        ),
      },
    ];
  }, [tableData]);

  const {
    state,
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page: tablePage,
    rows,
    prepareRow,
    setHiddenColumns,
    setPageSize,
  } = useTable(
    {
      columns,
      data: remainingData,
      initialState: {
        pageSize: 100,
        hiddenColumns: [!showRegion ? "region" : ""],
      },
    },
    usePagination,
  );

  return (
    <div {...getTableProps({ className: "content-section ReactTable ugg-table-2 leaderboard_table" })}>
      <div className="rt-thead -header">
        {headerGroups.map((headerGroup, index) => (
          <div {...headerGroup.getHeaderGroupProps({ className: "rt-tr" })}>
            {headerGroup.headers.map((column, index) => {
              const thClassNames = classnames(["rt-th", column.responsiveClassName, column.headerClassName]);

              return (
                <div {...column.getHeaderProps({ className: thClassNames, style: columnStyle(column) })}>
                  {column.render("Header")}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div {...getTableBodyProps({ className: "rt-tbody" })}>
        {tablePage.map((row, i) => {
          prepareRow(row);
          const [searchedRiotUserName, searchedRiotTagLine] = splitRiotId(user || "");
          const rowSummoner = { riotUserName: row.original.riotUserName, riotTagLine: row.original.riotTagLine };
          const isSearchedSummoner =
            searchedRiotUserName.toLowerCase() === rowSummoner.riotUserName.toLowerCase() &&
            searchedRiotTagLine.toLowerCase() === rowSummoner.riotTagLine.toLowerCase();

          return (
            <TableSummonerRow
              key={rowSummoner.riotUserName + rowSummoner.riotTagLine}
              rowIndex={i}
              row={row}
              isSearchedSummoner={isSearchedSummoner}
            />
          );
        })}
      </div>
      {!hidePagination && (
        <LeaderboardPagination
          firstEntryRanking={firstEntryRanking}
          lastEntryRanking={lastEntryRanking}
          totalCount={totalCount}
          currentPage={page}
        />
      )}
    </div>
  );
};

export default LeaderboardTable;
